import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { StyledDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { createMIPLoanDefinition } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	DataLoanDefinitionTct,
	LoanDefinitionType,
	PersonType,
} from '@dealstack/shared/lib/generated';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, LoadingOverlay } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import { z } from 'zod';

interface Props {
	opened: boolean;
	onClose: () => void;
	onSuccess: (createdLoanDefinition: DataLoanDefinitionTct) => void;
}

export const CreateLoanDefinitionShape = z.object({
	name: z.string(),
	funderId: z.string(),
	mipId: z.string(),
});

type CreateLoanDefinitionForm = z.infer<typeof CreateLoanDefinitionShape>;

export const CreateLoanDrawer = ({ opened, onClose, onSuccess }: Props) => {
	const tMipManage = useTranslationMipManage();

	const createLoanRequest = useMutation({
		mutationFn: (variables: Parameters<typeof createMIPLoanDefinition>[0]) =>
			createMIPLoanDefinition(variables),
	});

	const { mipSelectOptions, personsSelectOptions } = useContext(CapManageControllerContext);

	const legalPersonsOptions = useMemo(() => {
		return personsSelectOptions.filter((p) => p.type === PersonType.LEGAL);
	}, [personsSelectOptions]);
	const mipSelectOptionsFiltered = mipSelectOptions.filter((mip) => !!mip.value);

	const createLoanForm = useForm<CreateLoanDefinitionForm>({
		mode: 'all',
		resolver: zodResolver(CreateLoanDefinitionShape),
	});

	const onSubmit = async (loanDefinition: CreateLoanDefinitionForm) => {
		createLoanRequest.mutate(
			{
				loansDefinitions: [
					{
						type: LoanDefinitionType.MANAGEMENT_LOAN,
						name: loanDefinition.name,
						mipId: loanDefinition.mipId,
						funderPersonId: loanDefinition.funderId,
					},
				],
			},
			{
				onSuccess: (definitions) => {
					onSuccess(definitions[0]);
					showToastV2();
				},
			}
		);
	};

	useEffect(() => {
		createLoanForm.reset();
	}, [opened]);

	return (
		<StyledDrawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="md"
			transitionProps={drawerTransitionProps}
			title={tMipManage('root.loans.drawer.title.add')}
		>
			<Flex direction="column" h="100%" mih={400}>
				<LoadingOverlay visible={createLoanRequest.isPending} overlayProps={{ blur: 2 }} />

				<FormProvider {...createLoanForm}>
					<Flex direction="column" gap="1rem">
						<TextInput
							label={tMipManage('root.loans.drawer.label.name')}
							placeholder={tMipManage('root.loans.drawer.placeholder.name')}
							size="md"
							name="name"
							disabled={createLoanRequest.isPending}
						/>

						<Select
							label={tMipManage('root.loans.drawer.label.mip')}
							placeholder={tMipManage('root.loans.drawer.placeholder.mip')}
							size="md"
							searchable
							name="mipId"
							data={mipSelectOptionsFiltered}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							disabled={createLoanRequest.isPending}
						/>

						<Select
							label={tMipManage('root.loans.drawer.label.lender')}
							placeholder={tMipManage('root.loans.drawer.placeholder.lender')}
							size="md"
							searchable
							name="funderId"
							data={legalPersonsOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							disabled={createLoanRequest.isPending}
						/>
					</Flex>

					<Button
						fullWidth
						color="blue"
						onClick={createLoanForm.handleSubmit(onSubmit)}
						disabled={createLoanRequest.isPending}
						loading={createLoanRequest.isPending}
						mt="auto"
					>
						{tMipManage('root.loans.drawer.button.add')}
					</Button>
				</FormProvider>
			</Flex>
		</StyledDrawer>
	);
};
